import type { TokenSet } from '../api/types.js'

/**
 * Хранилище ключ-значение, которым владеет вызывающий. Тесты подставляют
 * `Map`, страница — обёртку над `sessionStorage`/`localStorage`: форма
 * методов совпадает, конкретный источник данных сессии не касается.
 */
export interface KeyValueStorage {
  get(key: string): string | undefined | null
  set(key: string, value: string): void
  delete(key: string): void
}

export interface SessionStore {
  /**
   * `permissions` необязателен: `/auth/refresh` возвращает только пару
   * токенов, и молчаливое продление сессии не должно стирать уже выданные
   * права — только вход и явное обновление прав их меняют.
   */
  set(tokens: TokenSet & { permissions?: string[] }): void
  clear(): void
  /** Токен доступа — только из памяти вкладки, не из хранилища (FR-ADM-003). */
  token(): string | null
  refreshToken(): string | null
  permissions(): string[]
}

const REFRESH_KEY = 'rk-admin.refresh-token'
const PERMISSIONS_KEY = 'rk-admin.permissions'

function readString(storage: KeyValueStorage, key: string): string | null {
  const value = storage.get(key)
  return value === undefined || value === null ? null : value
}

function readPermissions(storage: KeyValueStorage): string[] {
  const raw = readString(storage, PERMISSIONS_KEY)
  if (raw === null) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
  } catch {
    // Хранилище пережило несовместимое обновление формата — пустой список
    // прав безопаснее, чем отказ всей сессии из-за нечитаемой строки.
    return []
  }
}

/**
 * Сессия админки: токен доступа живёт минуты, поэтому держим его только
 * в замыкании этой функции — он обнуляется вместе с вкладкой (FR-ADM-003).
 * Refresh-токен и права переживают перезагрузку через переданное хранилище,
 * иначе человека выкидывало бы на экран входа при каждом обновлении страницы.
 */
export function createSession(storage: KeyValueStorage): SessionStore {
  let accessToken: string | null = null
  let permissions: string[] = readPermissions(storage)

  return {
    set(tokens) {
      accessToken = tokens.accessToken
      storage.set(REFRESH_KEY, tokens.refreshToken)
      if (tokens.permissions !== undefined) {
        permissions = tokens.permissions
        storage.set(PERMISSIONS_KEY, JSON.stringify(tokens.permissions))
      }
    },
    clear() {
      accessToken = null
      permissions = []
      storage.delete(REFRESH_KEY)
      storage.delete(PERMISSIONS_KEY)
    },
    token() {
      return accessToken
    },
    refreshToken() {
      return readString(storage, REFRESH_KEY)
    },
    permissions() {
      return permissions
    },
  }
}
